// --- Workspace canvas — the CanvasDirective contract (Canvas P0, SC-2653) -----
//
// The table already works this way: the agent's `get_table_slice` tool emits a
// `ViewDirective` (table.ts) over the chat SSE stream, an `isViewDirective`
// guard vets it, and `tableViewStore.applyDirective` applies it. `CanvasDirective`
// GENERALIZES that from "the table" to "the canvas" — one dispatch shape that any
// producer (a nav click, a dev mock, and eventually the BE `open_view` tool) maps
// onto. We WRAP `ViewDirective`, we don't replace it: the table's directive becomes
// the `params` for the entities view (compose, don't duplicate).
//
// P0 builds the CONSUMER (this contract + `workspaceStore`). `isCanvasDirective`
// is defined + tested now but only wired on the live SSE path at P6 (the BE swap);
// until then the entities directive rides the existing `isViewDirective` guard and
// is wrapped locally in `useAgentChat`.

import { isViewDirective, type ViewDirective } from './table';
import type { FindingStatus, Scope } from './index';

// Generic + STABLE view identifiers — route segments never localize. The DISPLAY
// label ("Products", "Athletes", "Fuses"…) comes from the catalog's entity term,
// decoupled from the view id (tech-plan §URL). Today's routes `table`/`changes`
// map onto `entities`/`history` at the P2 route migration.
export const CANVAS_VIEWS = [
  'entities',
  'attributes',
  'findings',
  'history',
  'profile',
] as const;

/** The closed set of system views (the five canonical sections). */
export type CoreCanvasView = (typeof CANVAS_VIEWS)[number];

// --- Board views (SC-2872) ----------------------------------------------------
// Generative boards mount as canvas views in the SAME id space the URL, the
// workspace store, and `canvas_context` already speak — a board's view id is
// `board:<boardId>`. The URL form is `/catalogs/:id/boards/:boardId`
// (`canvasNav.canvasViewPath` maps id ↔ path); the store/snapshot form is the
// prefixed id, so every existing `CanvasView` consumer (activeView, directives,
// snapshots) addresses boards with zero new plumbing.
export type BoardCanvasView = `board:${string}`;

export type CanvasView = CoreCanvasView | BoardCanvasView;

export function isCoreCanvasView(x: unknown): x is CoreCanvasView {
  return typeof x === 'string' && (CANVAS_VIEWS as readonly string[]).includes(x);
}

export function isBoardCanvasView(x: unknown): x is BoardCanvasView {
  return typeof x === 'string' && x.startsWith('board:') && x.length > 'board:'.length;
}

/** A board id → its canvas view id. */
export const boardCanvasView = (boardId: string): BoardCanvasView => `board:${boardId}`;

/** The board id inside a board view id, or `null` for a core view. */
export const boardIdOfView = (view: CanvasView): string | null =>
  isBoardCanvasView(view) ? view.slice('board:'.length) : null;

// A detail overlay layered over the active view, or `null` for none.
export type CanvasOverlay =
  | { kind: 'entity'; id: string }
  | { kind: 'attribute'; field: string }
  | { kind: 'finding'; id: string }
  | null;

// Findings filter axes — the WIRE mirror of the FE app model (`FindingsFilters` in
// findingFilters.ts) AND the BE `FindingsParams` (canvas/schemas.py). All three declare the
// same vocabulary independently: a types-barrel cycle (findingFilters → types → canvas) blocks
// importing findingFilters here, so the runtime member lists live inline — kept honest by the
// shared `FindingStatus`/`Scope` annotations (a stray member is a compile error); `kind`/sev/sort
// are small + stable. The categorical axes are MULTI-SELECT arrays (SC-2767); `null`/absent ≡
// unconstrained. `minSeverity` is a single-select floor with NO 'all' on the wire — the BE sends
// `null` for "no floor" and the consumer maps absent → the FE `'all'` default. `status` is
// special: its FE default is the pending queue, so an absent `status` ⇒ pending (NOT all) — the
// consumer (findingsDirectiveToSearch) honors that, matching the agent-facing tool description.
const FINDINGS_STATUS: readonly FindingStatus[] = ['pending', 'acknowledged', 'applied', 'dismissed'];
const FINDINGS_SCOPE: readonly Scope[] = ['value', 'field', 'catalog'];
const FINDINGS_KIND = ['fix', 'review'] as const;
const FINDINGS_MIN_SEVERITY = ['low', 'medium', 'high', 'critical'] as const;
// SC-3532 — sort is a table COLUMN + direction. 'queue' is retired but still accepted off the
// wire (a board drilldown or chat directive stored before then) and read as the default order.
const FINDINGS_SORT = ['queue', 'severity', 'affected', 'field'] as const;
const FINDINGS_SORT_DIR = ['asc', 'desc'] as const;

export type FindingsKind = (typeof FINDINGS_KIND)[number];
export type FindingsMinSeverity = (typeof FINDINGS_MIN_SEVERITY)[number];
export type FindingsSort = (typeof FINDINGS_SORT)[number];
export type FindingsSortDirection = (typeof FINDINGS_SORT_DIR)[number];

// Per-view, optional filter/sort intent. Discriminated on `view` — standardized on
// the same `CanvasView` ids as the top-level `view` (the tech-plan draft used
// `entities`/`products` inconsistently; `entities` wins). A bare `{ view }`
// directive with no params is a plain navigation.
export type CanvasDirectiveParams =
  | { view: 'entities'; table: ViewDirective } // reuse the existing table shape
  | {
      view: 'findings';
      // Multi-select axes (SC-2767) — `null`/absent ≡ unconstrained. See the vocab note above.
      status?: FindingStatus[] | null;
      scope?: Scope[] | null;
      kind?: FindingsKind[] | null;
      minSeverity?: FindingsMinSeverity | null; // floor; no 'all' on the wire
      sort?: FindingsSort | null;
      sortDir?: FindingsSortDirection | null; // absent ⇒ the column's natural direction
      field?: string[] | null; // SC-3533 — exact field names, free vocabulary
    }
  | { view: 'history'; since?: string /* ISO */ }
  | { view: 'attributes'; sort?: 'fill' | 'name' | 'findings' };

/** The findings branch of {@link CanvasDirectiveParams}, named so the URL encoder
 *  (`findingsDirectiveToSearch`, findingFilters.ts) can consume it without redeclaring. */
export type FindingsDirectiveParams = Extract<CanvasDirectiveParams, { view: 'findings' }>;

// What "a producer wants shown". `source` is the whole "don't feel lost" trick:
// `source: 'agent'` records provenance (→ banner + Clear once P4 renders it);
// `source: 'user'` is a clean view. Same field the table caption gates on today
// (`narration !== null`), lifted to the canvas.
export interface CanvasDirective {
  view: CanvasView;
  params?: CanvasDirectiveParams | null; // `null` (the BE wire shape for "no filter") ≡ absent, like `overlay`
  overlay?: CanvasOverlay; // optional: also open (or, when null, close) a detail overlay
  narration: string; // "" allowed; only `source` decides whether provenance shows
  source: 'user' | 'agent';
}

export function isCanvasView(x: unknown): x is CanvasView {
  return isCoreCanvasView(x) || isBoardCanvasView(x);
}

// --- Ambient canvas context (SC-2853, the FE of BE SC-2753) -------------------
//
// A send-time snapshot of the workspace, attached as `canvas_context` beside each
// chat message so the agent knows what the user is looking at ("this finding").
// The FE mirror of the BE `CanvasContext` (fe-handoff.md). Ids + labels ONLY —
// the label is the display line the view already renders (finding summary, entity
// identifier); never row values or projected objects (the agent's tools fetch
// substance server-side, tenant-scoped). The BE is fully lenient: absent or
// malformed context can never 422 a message — worst case the turn is context-blind.

/** One object the user can be looking at / has selected. Entity ids are the
 *  BUSINESS identifier (SKU), never the Mongo id — same rule as apply_finding
 *  (SC-2594). `attribute` refs carry the column name instead of id/label.
 *  `label` is best-effort: agent-/URL-opened overlays may not have one. */
export type CanvasContextRef =
  | { kind: 'entity'; id: string; label?: string }
  | { kind: 'finding'; id: string; label?: string }
  | { kind: 'attribute'; field: string };

/** The `canvas_context` field POSTed beside `content` on a chat message. `params`
 *  is the SAME per-view shape `applyCanvasDirective` consumes, so the agent reads
 *  the view state in the vocabulary it already speaks (`open_view`'s). */
export interface CanvasContextSnapshot {
  view: CanvasView;
  params?: CanvasDirectiveParams;
  overlay?: CanvasContextRef;
  /** Selected rows. UNUSED until the table grows row selection — the BE renders
   *  at most the first 25 refs, so bulk gestures should stay filter-shaped. */
  selection?: CanvasContextRef[];
}

/** A concrete (non-null) overlay shape. `null`/absent overlays are handled by the
 *  caller — this only vets a present, non-null overlay. */
function isConcreteOverlay(x: unknown): x is Exclude<CanvasOverlay, null> {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  switch (o.kind) {
    case 'entity':
    case 'finding':
      return typeof o.id === 'string';
    case 'attribute':
      return typeof o.field === 'string';
    default:
      return false;
  }
}

/** A multi-select axis off the wire: absent, `null`, or an array whose every member is a
 *  known literal. Fails CLOSED on a bogus member (the BE is Literal-typed, so this only
 *  guards against real drift) — consistent with the scalar-enum checks below. */
const isListOfOrNil = (x: unknown, allowed: readonly string[]): boolean =>
  x === undefined ||
  x === null ||
  (Array.isArray(x) && x.every((v) => typeof v === 'string' && allowed.includes(v)));

/** A single-select axis: absent, `null`, or one known literal. */
const isMemberOrNil = (x: unknown, allowed: readonly string[]): boolean =>
  x === undefined || x === null || (typeof x === 'string' && allowed.includes(x));

function isCanvasDirectiveParams(x: unknown): x is CanvasDirectiveParams {
  if (!x || typeof x !== 'object') return false;
  const p = x as Record<string, unknown>;
  switch (p.view) {
    case 'entities':
      // The entities params wrap a full ViewDirective — vet it with the same
      // guard the table's own SSE path uses, so the shapes can't drift apart.
      return isViewDirective(p.table);
    case 'findings':
      // The multi-select findings filters (SC-2767): status/scope/kind are lists, minSeverity
      // is a floor, sort an order — each optional + nullable (the BE omits/nulls an unconstrained
      // axis). Members validated against the inline vocab; unknowns fail closed.
      return (
        isListOfOrNil(p.status, FINDINGS_STATUS) &&
        isListOfOrNil(p.scope, FINDINGS_SCOPE) &&
        isListOfOrNil(p.kind, FINDINGS_KIND) &&
        isMemberOrNil(p.minSeverity, FINDINGS_MIN_SEVERITY) &&
        isMemberOrNil(p.sort, FINDINGS_SORT) &&
        isMemberOrNil(p.sortDir, FINDINGS_SORT_DIR) &&
        // Field names have no fixed vocabulary — any list of strings.
        (p.field === undefined ||
          p.field === null ||
          (Array.isArray(p.field) && p.field.every((v) => typeof v === 'string')))
      );
    case 'history':
      return p.since === undefined || typeof p.since === 'string';
    case 'attributes':
      return (
        p.sort === undefined || p.sort === 'fill' || p.sort === 'name' || p.sort === 'findings'
      );
    default:
      return false;
  }
}

/**
 * Structural guard for a `CanvasDirective` off the wire (the future BE `open_view`
 * tool result, P6). Fails CLOSED on any drift: an unknown `view`, a non-string
 * `narration`, a bad `source`, a malformed present `overlay`, or `params` that
 * don't match a known view's shape. `overlay`/`params` are optional — absent OR
 * `null` is valid for either; only a PRESENT, non-null one is vetted. (The BE tool
 * runner serializes with `model_dump(mode="json")` and no `exclude_none`, so a plain
 * navigation with no filter arrives as `params: null` — an explicit null key, not an
 * absent one. Treat it the same as absent, exactly as we already do for `overlay`;
 * without this, every no-filter `open_view` would fail closed and be dropped.)
 */
export function isCanvasDirective(x: unknown): x is CanvasDirective {
  if (!x || typeof x !== 'object') return false;
  const c = x as Record<string, unknown>;
  if (!isCanvasView(c.view)) return false;
  if (typeof c.narration !== 'string') return false;
  if (c.source !== 'user' && c.source !== 'agent') return false;
  if (c.overlay !== undefined && c.overlay !== null && !isConcreteOverlay(c.overlay)) return false;
  if (c.params !== undefined && c.params !== null) {
    if (!isCanvasDirectiveParams(c.params)) return false;
    // The params discriminant must match the top-level view: they address the same
    // view, so a `view: 'history'` directive carrying `params.view: 'entities'` is
    // incoherent — accepting it would let the hub forward the table query (keyed on
    // `params.view`) while switching `activeView` to something else.
    if (c.params.view !== c.view) return false;
  }
  return true;
}
